import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import { getStoredTokens } from '@/auth/token';
import { login, refreshAccessToken } from '@/auth/oidc';

export function useAuth() {
  const { user, isAuthenticated } = useAuthStore();
  const [authError, setAuthError] = useState<string | null>(null);
  const isRealAuth = import.meta.env.VITE_SKIP_AUTH !== 'true';
  const [initializing, setInitializing] = useState(!isAuthenticated && isRealAuth);
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated || !isRealAuth) {
      setInitializing(false);
      return;
    }

    // Guard against React StrictMode double-mount
    if (redirectedRef.current) return;
    redirectedRef.current = true;

    // Not authenticated — check if we have a refresh token for silent refresh
    const { refreshToken } = getStoredTokens();
    if (refreshToken) {
      refreshAccessToken().then((ok) => {
        if (ok) {
          const { accessToken, idToken } = getStoredTokens();
          if (accessToken && idToken) {
            useAuthStore.getState().updateToken(accessToken, idToken);
            usePermissionsStore.getState().fetchPermissions();
          }
          setInitializing(false);
        } else {
          // Refresh failed — redirect to Keycloak
          login().catch(() => {
            setAuthError('Kan geen verbinding maken met de authenticatieserver.');
            setInitializing(false);
          });
        }
      });
    } else {
      // No tokens at all — redirect to Keycloak login
      login().catch(() => {
        setAuthError('Kan geen verbinding maken met de authenticatieserver.');
        setInitializing(false);
      });
    }
  }, [isAuthenticated, isRealAuth]);

  return { user, isAuthenticated, authError, initializing };
}
